import { generationActionGuard } from './generation-action-guard.js';
import { useState, type ReactNode, type Ref } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible.js';
import { Input } from '@/components/ui/input.js';
import { cn } from '@/lib/utils.js';
import { Switch } from '@/components/ui/switch.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.js';
import type {
  IterationFailurePolicy,
  RunExecutionMode,
  WebNetworkCaptureMode,
  WebNetworkCaptureOverride,
} from '@/lib/api/quality-types.js';

const PROJECT_DEFAULT_CAPTURE_VALUE = 'project-default';
const INHERITED_EXECUTION_VALUE = 'inherited';

const EXECUTION_MODE_LABELS: Record<RunExecutionMode, string> = {
  parallel: 'Parallel',
  sequential: 'One case at a time',
};

/** A newer service may answer with a mode this build does not know; treat it as the default. */
export function normalizeRunExecutionMode(value: unknown): RunExecutionMode {
  return value === 'sequential' ? 'sequential' : 'parallel';
}

/** The upper bound the service accepts for `repeatCount` (MEDA-1288). */
export const MAX_REPEAT_COUNT = 50;

/** The repeat count the field text names, or null when it is not a whole number from 1 to 50. */
export function parseRepeatCount(text: string): number | null {
  if (!/^\d+$/.test(text.trim())) return null;
  const count = Number(text);
  return count >= 1 && count <= MAX_REPEAT_COUNT ? count : null;
}

/** How many times each case runs in this launch, and what happens after a failed iteration. */
export interface RepeatControl {
  /** The field text as typed, so an out-of-range value stays on screen until it is fixed. */
  value: string;
  onChange: (value: string) => void;
  onIterationFailure: IterationFailurePolicy;
  onIterationFailureChange: (value: IterationFailurePolicy) => void;
  /** Set after Launch was clicked with a value outside 1..50. */
  showError: boolean;
  /** Parallel iterations already running finish after a stop, so the stop note says so. */
  parallel: boolean;
  inputRef?: Ref<HTMLInputElement>;
}

/** Per-run case execution order. Without `onChange` the inherited value is shown read-only. */
export interface ExecutionOrderControl {
  inherited: RunExecutionMode;
  /** Null inherits; a mode is an explicit override for this run only. */
  value: RunExecutionMode | null;
  onChange?: (value: RunExecutionMode | null) => void;
}

export function RunAdvancedSettings({
  inheritedTakeoverEnabled = false,
  inheritanceLabel = 'workspace',
  executionInheritanceLabel,
  value = null,
  onChange,
  disabled = false,
  explainUnavailableActions = false,
  webNetworkCapture,
  executionOrder,
  variables,
  inline = false,
  executionFooter,
  repeat,
}: {
  inline?: boolean;
  /** The launch's repeat count, shown in the Execution section when given. */
  repeat?: RepeatControl;
  executionFooter?: ReactNode;
  inheritedTakeoverEnabled?: boolean;
  inheritanceLabel?: string;
  executionInheritanceLabel?: string;
  value?: boolean | null;
  onChange?: (value: boolean) => void;
  disabled?: boolean;
  explainUnavailableActions?: boolean;
  webNetworkCapture?: {
    value: WebNetworkCaptureOverride;
    onChange: (value: WebNetworkCaptureOverride) => void;
    defaultLabel?: string;
    disabled?: boolean;
  };
  executionOrder?: ExecutionOrderControl;
  /** The launch's per-run variable overrides, shown as the last section when given. */
  variables?: ReactNode;
}) {
  const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false);
  const effectiveTakeoverEnabled = value ?? inheritedTakeoverEnabled;
  const executionControl = executionOrder ? (
    <div
      className={
        inline
          ? 'grid grid-cols-[144px_minmax(0,1fr)] max-[600px]:grid-cols-1 items-center gap-x-3 gap-y-2'
          : 'flex flex-col gap-2'
      }
    >
      <span className="text-sm font-medium text-foreground">
        Case execution
      </span>
      {executionOrder.onChange ? (
        <Select
          disabled={!explainUnavailableActions && disabled}
          value={executionOrder.value ?? INHERITED_EXECUTION_VALUE}
          onValueChange={(selected) =>
            executionOrder.onChange?.(
              selected === INHERITED_EXECUTION_VALUE
                ? null
                : (selected as RunExecutionMode),
            )
          }
        >
          <SelectTrigger
            {...generationActionGuard(
              explainUnavailableActions && disabled,
              'Case execution settings are unavailable until a compatible Quality service is connected.',
            )}
            aria-label="Case execution"
            className={inline ? 'w-full min-w-0' : undefined}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={INHERITED_EXECUTION_VALUE}>
              Inherited from {executionInheritanceLabel ?? inheritanceLabel}:{' '}
              {
                EXECUTION_MODE_LABELS[
                  normalizeRunExecutionMode(executionOrder.inherited)
                ]
              }
            </SelectItem>
            <SelectItem value="parallel">
              {EXECUTION_MODE_LABELS.parallel}
            </SelectItem>
            <SelectItem value="sequential">
              {EXECUTION_MODE_LABELS.sequential}
            </SelectItem>
          </SelectContent>
        </Select>
      ) : (
        <p className="text-xs text-muted-foreground">
          Inherited from {inheritanceLabel}:{' '}
          {
            EXECUTION_MODE_LABELS[
              normalizeRunExecutionMode(executionOrder.inherited)
            ]
          }
          .
        </p>
      )}
      {!inline && (
        <p className="text-xs text-muted-foreground">
          One case at a time starts the next case only after the previous one
          has finished, in suite order. Parallel runs every case as soon as a
          worker is free.
        </p>
      )}
      {executionFooter && (
        <div
          className={inline ? 'col-start-2 max-[600px]:col-start-1' : undefined}
        >
          {executionFooter}
        </div>
      )}
    </div>
  ) : null;
  const repeatCount = repeat ? parseRepeatCount(repeat.value) : null;
  const repeatControl = repeat ? (
    <div
      className={
        inline
          ? 'grid grid-cols-[144px_minmax(0,1fr)] max-[600px]:grid-cols-1 items-center gap-x-3 gap-y-2'
          : 'flex flex-col gap-2'
      }
    >
      <label
        htmlFor="launch-repeat-count"
        className="text-sm font-medium text-foreground"
      >
        Repeat
      </label>
      <span className="flex items-center gap-2 text-sm text-muted-foreground">
        <Input
          id="launch-repeat-count"
          ref={repeat.inputRef}
          type="number"
          inputMode="numeric"
          min={1}
          max={MAX_REPEAT_COUNT}
          step={1}
          className="w-20"
          value={repeat.value}
          aria-invalid={
            repeat.showError && repeatCount === null ? true : undefined
          }
          aria-describedby={
            repeat.showError && repeatCount === null
              ? 'launch-repeat-count-error'
              : undefined
          }
          onChange={(event) => repeat.onChange(event.target.value)}
        />
        times · 1 to {MAX_REPEAT_COUNT}
      </span>
      {repeat.showError && repeatCount === null ? (
        <p
          id="launch-repeat-count-error"
          role="alert"
          className={cn(
            'text-xs text-destructive',
            inline && 'col-start-2 max-[600px]:col-start-1',
          )}
        >
          Enter a repeat count from 1 to {MAX_REPEAT_COUNT}.
        </p>
      ) : null}
      {repeatCount !== null && repeatCount > 1 ? (
        <>
          <span className="text-sm font-medium text-foreground">
            After a failed iteration
          </span>
          <Select
            value={repeat.onIterationFailure}
            onValueChange={(selected) =>
              repeat.onIterationFailureChange(
                selected as IterationFailurePolicy,
              )
            }
          >
            <SelectTrigger
              aria-label="After a failed iteration"
              className={inline ? 'w-full min-w-0' : undefined}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="stop">
                Stop the remaining iterations
              </SelectItem>
              <SelectItem value="continue">Run every iteration</SelectItem>
            </SelectContent>
          </Select>
          <div
            className={cn(
              'space-y-1 text-xs text-muted-foreground',
              inline && 'col-start-2 max-[600px]:col-start-1',
            )}
          >
            <p>
              {repeat.onIterationFailure === 'continue'
                ? 'Every iteration runs, even after a failure.'
                : repeat.parallel
                  ? 'Iterations already running finish; only those not started yet are skipped. For an exact stop, choose One case at a time.'
                  : 'The remaining iterations are skipped and shown as Not run.'}
            </p>
            <p>
              Use <code>{'{{ITERATION}}'}</code> or <code>{'{{UNIQUE}}'}</code>{' '}
              in case text so each iteration enters different data.
            </p>
            <p>
              Results are not written back to the test management system or
              ReportPortal for repeated runs.
            </p>
          </div>
        </>
      ) : null}
    </div>
  ) : null;
  return (
    <Collapsible
      open={inline || advancedSettingsOpen}
      onOpenChange={setAdvancedSettingsOpen}
      className={inline ? '' : 'rounded-lg border border-border'}
    >
      {!inline && (
        <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left text-sm font-medium transition-colors hover:bg-muted/30">
          <span>Advanced settings</span>
          <ChevronDownIcon
            className={`size-4 text-muted-foreground transition-transform ${
              advancedSettingsOpen ? 'rotate-180' : ''
            }`}
            aria-hidden
          />
        </CollapsibleTrigger>
      )}
      <CollapsibleContent
        className={inline ? '' : 'border-t border-border px-4 py-3'}
      >
        <div className={inline ? 'flex flex-col gap-2' : 'flex flex-col gap-4'}>
          {inline && (executionControl || repeatControl) && (
            <section className="space-y-2 border-t pt-2">
              <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Execution
              </h4>
              {executionControl}
              {repeatControl}
            </section>
          )}
          {onChange ? (
            <div className="flex flex-col gap-2">
              <label
                className={
                  inline
                    ? 'grid min-h-9 grid-cols-[144px_minmax(0,1fr)] max-[600px]:grid-cols-1 items-center gap-3'
                    : 'flex items-center justify-between gap-4'
                }
              >
                <span className="flex flex-col gap-0.5">
                  <span className="text-sm font-medium text-foreground">
                    Human-in-the-loop
                  </span>
                  {!inline && (
                    <span className="text-xs text-muted-foreground">
                      Inherited from {inheritanceLabel}:{' '}
                      {inheritedTakeoverEnabled ? 'On' : 'Off'}. An explicit
                      choice here overrides that default for this run only.
                    </span>
                  )}
                </span>
                <Switch
                  className={inline ? 'justify-self-start' : undefined}
                  disabled={!explainUnavailableActions && disabled}
                  {...generationActionGuard(
                    explainUnavailableActions && disabled,
                    'Human-in-the-loop is unavailable until a compatible Quality service is connected.',
                  )}
                  checked={effectiveTakeoverEnabled}
                  onCheckedChange={onChange}
                  aria-label="Human-in-the-loop"
                />
              </label>
              {disabled && !inline && (
                <p className="text-xs text-muted-foreground">
                  This setting is unavailable until a compatible Quality service
                  is connected.
                </p>
              )}
              {!inline && (
                <p className="text-xs text-muted-foreground">
                  Allow the run to ask for missing input or request video
                  takeover when it cannot continue.
                </p>
              )}
            </div>
          ) : null}
          {!inline && executionControl}
          {!inline && repeatControl}
          {webNetworkCapture ? (
            <div
              className={
                inline
                  ? 'grid grid-cols-[144px_minmax(0,1fr)] max-[600px]:grid-cols-1 items-center gap-x-3 gap-y-2'
                  : 'flex flex-col gap-2'
              }
            >
              <span className="text-sm font-medium text-foreground">
                Web network diagnostics
              </span>
              <Select
                disabled={
                  !explainUnavailableActions && webNetworkCapture.disabled
                }
                value={webNetworkCapture.value ?? PROJECT_DEFAULT_CAPTURE_VALUE}
                onValueChange={(selected) =>
                  webNetworkCapture.onChange(
                    selected === PROJECT_DEFAULT_CAPTURE_VALUE
                      ? null
                      : (selected as WebNetworkCaptureMode),
                  )
                }
              >
                <SelectTrigger
                  {...generationActionGuard(
                    explainUnavailableActions && webNetworkCapture.disabled,
                    'Web network diagnostics are unavailable until a compatible Quality service is connected.',
                  )}
                  aria-label="Web network diagnostics"
                  className={inline ? 'w-full min-w-0' : undefined}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={PROJECT_DEFAULT_CAPTURE_VALUE}>
                    {webNetworkCapture.defaultLabel ?? 'Use project default'}
                  </SelectItem>
                  <SelectItem value="errors-only">Errors only</SelectItem>
                  <SelectItem value="full-har">Full HAR</SelectItem>
                </SelectContent>
              </Select>
              {!inline && (
                <p className="text-xs text-muted-foreground">
                  Errors only saves HTTP-error details and extracted messages.
                  Full HAR records all network requests and responses internally
                  and can use more memory. Neither mode publishes raw capture
                  files.
                </p>
              )}
            </div>
          ) : null}
          {variables ? (
            <div
              className={
                inline
                  ? 'flex flex-col gap-2 border-t pt-3'
                  : 'flex flex-col gap-2'
              }
            >
              <span className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-foreground">
                  Variables
                </span>
                <span className="text-xs text-muted-foreground">
                  {inline
                    ? 'Override values for this run.'
                    : 'The workspace variables these tests use. Switch one on to change its value for this run only.'}
                </span>
              </span>
              {variables}
            </div>
          ) : null}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
